<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import AnalyticsPanel from '../analytics/AnalyticsPanel.vue';
import { useAnalyticsFormat } from '../analytics/useAnalyticsFormat';
import AgoraConversas from './AgoraConversas.vue';
import { teamLabel } from './atendimentoLabels';
import { QUEM_CORES } from './quemCores';

// "Agora": com quem está cada conversa neste minuto. Os números são contagens do
// momento — zero aqui é zero de verdade (ninguém esperando), não falta de dado.
// A conversa em que o contato respondeu a um disparo e a IA ainda não respondeu
// depois dele não está "Com a IA": tem linha própria, na cor do disparo (G3).
//
// Cada linha abre a lista das conversas dela (quem espera, com o tempo e o
// destino; as paradas; a linha do disparo; quem está com cada atendente; as
// abertas, as não atribuídas e as pendentes da conta), com o recorte da página:
// o total da lista de um atendente é o número da linha dele.
const props = defineProps({
  // A resposta de GET atendimento/agora; null enquanto não chegou.
  report: { type: Object, default: null },
  failed: { type: Boolean, default: false },
  // O recorte da página, para a lista pedir as mesmas conversas.
  filters: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const { number, duration } = useAnalyticsFormat();

const withAi = computed(() => props.report?.with_ai || null);
const outboundReplied = computed(() => props.report?.outbound_replied || null);
const waiting = computed(() => props.report?.waiting_for_team || null);
const withPerson = computed(() => props.report?.with_person || null);
const online = computed(() => props.report?.online || null);

// Toda conversa aberta da conta, e não só os ciclos de PowerAgent (D21): abertas
// e não atribuídas contam também as que estão com a IA. Pendentes é a fila
// humana (D20). Na caixa de robô externo (n8n), a aberta só entra em abertas e
// não atribuídas com uma pessoa nela; a que o robô segura tem linha própria
// (D34), que vem nula, e não aparece, na conta sem caixa de robô externo.
const hasExternalBot = computed(() =>
  Number.isFinite(props.report?.conversations?.with_external_bot)
);
const liveLines = computed(() => {
  const counts = props.report.conversations;
  const lines = [
    { key: 'open', label: t('ATENDIMENTO_REPORTS.NOW.LIVE.OPEN') },
    { key: 'unassigned', label: t('ATENDIMENTO_REPORTS.NOW.LIVE.UNASSIGNED') },
    { key: 'pending', label: t('ATENDIMENTO_REPORTS.NOW.LIVE.PENDING') },
  ];
  if (hasExternalBot.value)
    lines.push({
      key: 'with_external_bot',
      label: t('ATENDIMENTO_REPORTS.NOW.LIVE.WITH_EXTERNAL_BOT'),
    });
  return lines.map(line => ({ ...line, count: counts[line.key] }));
});

// O status de cada atendente vem do servidor (D22), na ordem em que a lista se
// agrupa. A cor vai sempre com a legenda.
const STATUS_DOT = {
  online: 'bg-n-teal-9',
  busy: 'bg-n-amber-9',
  offline: 'bg-n-slate-7',
};
const STATUS_ORDER = Object.keys(STATUS_DOT);
const statusLabel = status =>
  ({
    online: t('ATENDIMENTO_REPORTS.NOW.STATUS.ONLINE'),
    busy: t('ATENDIMENTO_REPORTS.NOW.STATUS.BUSY'),
    offline: t('ATENDIMENTO_REPORTS.NOW.STATUS.OFFLINE'),
  })[status];

const destinationName = destination => {
  if (destination.kind === 'none')
    return t('ATENDIMENTO_REPORTS.NOW.NO_DESTINATION');
  // O time sem nome é o que a conta apagou; a pessoa sem nome saiu da conta.
  if (!destination.name)
    return destination.kind === 'team'
      ? t('ATENDIMENTO_REPORTS.CHANNELS.REMOVED.TEAM')
      : t('ATENDIMENTO_REPORTS.NOW.REMOVED_USER');
  return destination.kind === 'team'
    ? teamLabel(destination.name)
    : destination.name;
};

const alerts = computed(() =>
  (waiting.value?.destinations || []).filter(item => item.nobody_online)
);

// Todos os atendentes, sem corte: o servidor já manda a lista inteira, os online
// primeiro e depois por nome. A tela agrupa por status (disponível, ocupado,
// desconectado), e o sort estável mantém o nome dentro de cada grupo.
const people = computed(() =>
  [...(online.value?.people || [])].sort(
    (a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status)
  )
);

// A chave da linha por atendente de "Com uma pessoa": a tela diz qual é.
const perPersonKey = computed(() =>
  withPerson.value?.per_person_key === 'assignee_or_first_responder'
    ? t('ATENDIMENTO_REPORTS.NOW.PER_PERSON_KEY.ASSIGNEE_OR_FIRST_RESPONDER')
    : ''
);

const listRef = ref(null);
const openList = (line, label, personId = null) =>
  listRef.value?.open(line, label, personId);
const subLine = (line, detail) =>
  t('ATENDIMENTO_REPORTS.NOW.LIST.SUBLINE', { line, detail });
</script>

<template>
  <AnalyticsPanel
    data-test-id="atendimento-agora"
    :title="t('ATENDIMENTO_REPORTS.NOW.TITLE')"
    :description="t('ATENDIMENTO_REPORTS.NOW.DESCRIPTION')"
  >
    <p v-if="failed" class="mb-0 text-sm text-n-ruby-11">
      {{ t('ATENDIMENTO_REPORTS.FAILED') }}
    </p>

    <div
      v-else-if="report"
      class="grid grid-cols-1 gap-3 min-w-0 sm:grid-cols-2 xl:grid-cols-4"
    >
      <section
        data-test-id="atendimento-agora-live"
        class="flex flex-col gap-1 p-3 min-w-0 rounded-lg border border-n-weak sm:col-span-2 xl:col-span-4"
      >
        <span class="text-sm text-n-slate-11">
          {{ t('ATENDIMENTO_REPORTS.NOW.LIVE.TITLE') }}
        </span>
        <div class="flex flex-wrap gap-x-8 gap-y-2">
          <div v-for="line in liveLines" :key="line.key" class="flex flex-col">
            <span class="text-xs text-n-slate-11">{{ line.label }}</span>
            <button
              type="button"
              :data-test-id="`atendimento-agora-open-${line.key}`"
              class="self-start text-2xl font-medium rounded tabular-nums text-n-slate-12 enabled:hover:underline"
              :disabled="!line.count"
              :title="t('ATENDIMENTO_REPORTS.NOW.LIST.OPEN')"
              @click="openList(line.key, line.label)"
            >
              {{ number(line.count) }}
            </button>
          </div>
        </div>
        <span class="text-xs text-n-slate-11">
          {{ t('ATENDIMENTO_REPORTS.NOW.LIVE.HINT') }}
        </span>
        <span v-if="hasExternalBot" class="text-xs text-n-slate-11">
          {{ t('ATENDIMENTO_REPORTS.NOW.LIVE.EXTERNAL_BOT_HINT') }}
        </span>
      </section>

      <section
        data-test-id="atendimento-agora-with-ai"
        class="flex flex-col gap-1 p-3 min-w-0 rounded-lg border border-n-weak"
      >
        <span class="flex gap-2 items-center text-sm text-n-slate-11">
          <span class="rounded-full size-2" :class="QUEM_CORES.ia.bg" />
          {{ t('ATENDIMENTO_REPORTS.NOW.WITH_AI') }}
        </span>
        <button
          type="button"
          data-test-id="atendimento-agora-open-with_ai"
          class="self-start text-2xl font-medium rounded tabular-nums text-n-slate-12 enabled:hover:underline"
          :disabled="!withAi.count"
          :title="t('ATENDIMENTO_REPORTS.NOW.LIST.OPEN')"
          @click="openList('with_ai', t('ATENDIMENTO_REPORTS.NOW.WITH_AI'))"
        >
          {{ number(withAi.count) }}
        </button>
        <span class="text-xs text-n-slate-11">
          {{ t('ATENDIMENTO_REPORTS.NOW.WITH_AI_HINT') }}
        </span>
        <ul class="flex flex-col gap-1 mt-2 text-sm list-none">
          <li
            v-for="agent in withAi.per_agent"
            :key="agent.id"
            class="flex gap-3 justify-between"
          >
            <span class="truncate text-n-slate-12">{{ agent.name }}</span>
            <span class="font-medium tabular-nums text-n-slate-12">
              {{ number(agent.count) }}
            </span>
          </li>
          <li data-test-id="atendimento-agora-stalled">
            <button
              type="button"
              data-test-id="atendimento-agora-open-stalled"
              class="flex gap-3 justify-between w-full rounded text-start text-n-slate-10 enabled:hover:bg-n-alpha-1"
              :disabled="!withAi.stalled"
              @click="
                openList(
                  'stalled',
                  subLine(
                    t('ATENDIMENTO_REPORTS.NOW.WITH_AI'),
                    t('ATENDIMENTO_REPORTS.NOW.STALLED')
                  )
                )
              "
            >
              <span>{{ t('ATENDIMENTO_REPORTS.NOW.STALLED') }}</span>
              <span class="tabular-nums">{{ number(withAi.stalled) }}</span>
            </button>
          </li>
        </ul>
      </section>

      <section
        v-if="outboundReplied"
        data-test-id="atendimento-agora-outbound-replied"
        class="flex flex-col gap-1 p-3 min-w-0 rounded-lg border border-n-weak"
      >
        <span class="flex gap-2 items-center text-sm text-n-slate-11">
          <span
            class="flex-shrink-0 rounded-full size-2"
            :class="QUEM_CORES.disparo.bg"
          />
          {{ t('ATENDIMENTO_REPORTS.NOW.OUTBOUND_REPLIED') }}
        </span>
        <button
          type="button"
          data-test-id="atendimento-agora-open-outbound_replied"
          class="self-start text-2xl font-medium rounded tabular-nums text-n-slate-12 enabled:hover:underline"
          :disabled="!outboundReplied.count"
          :title="t('ATENDIMENTO_REPORTS.NOW.LIST.OPEN')"
          @click="
            openList(
              'outbound_replied',
              t('ATENDIMENTO_REPORTS.NOW.OUTBOUND_REPLIED')
            )
          "
        >
          {{ number(outboundReplied.count) }}
        </button>
        <span
          data-test-id="atendimento-agora-outbound-replied-waiting"
          class="text-xs text-n-slate-11"
        >
          {{
            outboundReplied.waiting &&
            outboundReplied.oldest_wait_seconds !== null
              ? t('ATENDIMENTO_REPORTS.NOW.OUTBOUND_WAITING', {
                  count: number(outboundReplied.waiting),
                  time: duration(outboundReplied.oldest_wait_seconds),
                })
              : t('ATENDIMENTO_REPORTS.NOW.NOBODY_WAITING')
          }}
        </span>
        <ul class="flex flex-col gap-1 mt-2 text-sm list-none">
          <li
            v-for="agent in outboundReplied.per_agent"
            :key="agent.id"
            class="flex gap-3 justify-between"
          >
            <span class="truncate text-n-slate-12">{{ agent.name }}</span>
            <span class="font-medium tabular-nums text-n-slate-12">
              {{ number(agent.count) }}
            </span>
          </li>
          <li
            data-test-id="atendimento-agora-outbound-replied-stalled"
            class="flex gap-3 justify-between text-n-slate-10"
          >
            <span>{{ t('ATENDIMENTO_REPORTS.NOW.STALLED') }}</span>
            <span class="tabular-nums">
              {{ number(outboundReplied.stalled) }}
            </span>
          </li>
        </ul>
      </section>

      <section
        data-test-id="atendimento-agora-waiting"
        class="flex flex-col gap-1 p-3 min-w-0 rounded-lg border border-n-weak"
      >
        <span class="flex gap-2 items-center text-sm text-n-slate-11">
          <span class="rounded-full size-2" :class="QUEM_CORES.espera.bg" />
          {{ t('ATENDIMENTO_REPORTS.NOW.WAITING') }}
        </span>
        <button
          type="button"
          data-test-id="atendimento-agora-open-waiting"
          class="self-start text-2xl font-medium rounded tabular-nums text-n-slate-12 enabled:hover:underline"
          :disabled="!waiting.count"
          :title="t('ATENDIMENTO_REPORTS.NOW.LIST.OPEN')"
          @click="openList('waiting', t('ATENDIMENTO_REPORTS.NOW.WAITING'))"
        >
          {{ number(waiting.count) }}
        </button>
        <span class="text-xs text-n-slate-11">
          {{
            waiting.oldest_wait_seconds === null
              ? t('ATENDIMENTO_REPORTS.NOW.NOBODY_WAITING')
              : t('ATENDIMENTO_REPORTS.NOW.OLDEST_WAIT', {
                  time: duration(waiting.oldest_wait_seconds),
                })
          }}
        </span>
        <ul
          v-if="waiting.destinations.length"
          class="flex flex-col gap-1 mt-2 text-sm list-none"
        >
          <li
            v-for="destination in waiting.destinations"
            :key="`${destination.kind}-${destination.id}`"
            class="flex gap-3 justify-between"
          >
            <span class="truncate text-n-slate-12">
              {{ destinationName(destination) }}
            </span>
            <span class="font-medium tabular-nums text-n-slate-12">
              {{ number(destination.count) }}
            </span>
          </li>
        </ul>
        <p
          v-for="alert in alerts"
          :key="`alert-${alert.kind}-${alert.id}`"
          data-test-id="atendimento-agora-nobody-online"
          class="self-start px-1.5 py-0.5 mt-1 mb-0 max-w-full text-xs font-medium rounded break-words bg-n-ruby-3 text-n-ruby-11"
        >
          {{
            t('ATENDIMENTO_REPORTS.NOW.NOBODY_ONLINE', {
              name: destinationName(alert),
            })
          }}
        </p>
      </section>

      <section
        data-test-id="atendimento-agora-with-person"
        class="flex flex-col gap-1 p-3 min-w-0 rounded-lg border border-n-weak"
      >
        <span class="flex gap-2 items-center text-sm text-n-slate-11">
          <span class="rounded-full size-2" :class="QUEM_CORES.pessoa.bg" />
          {{ t('ATENDIMENTO_REPORTS.NOW.WITH_PERSON') }}
        </span>
        <button
          type="button"
          data-test-id="atendimento-agora-open-with_person"
          class="self-start text-2xl font-medium rounded tabular-nums text-n-slate-12 enabled:hover:underline"
          :disabled="!withPerson.count"
          :title="t('ATENDIMENTO_REPORTS.NOW.LIST.OPEN')"
          @click="
            openList('with_person', t('ATENDIMENTO_REPORTS.NOW.WITH_PERSON'))
          "
        >
          {{ number(withPerson.count) }}
        </button>
        <span class="text-xs text-n-slate-11">
          {{ t('ATENDIMENTO_REPORTS.NOW.WITH_PERSON_HINT') }}
        </span>
        <ul
          v-if="withPerson.per_person.length"
          class="flex flex-col gap-1 mt-2 text-sm list-none"
        >
          <li v-for="person in withPerson.per_person" :key="person.user_id">
            <!-- A pessoa removida não abre: a conta não a acha mais. -->
            <button
              type="button"
              :data-test-id="`atendimento-agora-person-${person.user_id}`"
              class="flex gap-3 justify-between w-full rounded text-start enabled:hover:bg-n-alpha-1"
              :disabled="!person.name || !person.count"
              @click="
                openList(
                  'with_person',
                  subLine(
                    t('ATENDIMENTO_REPORTS.NOW.WITH_PERSON'),
                    person.name
                  ),
                  person.user_id
                )
              "
            >
              <span class="truncate text-n-slate-12">
                {{ person.name || t('ATENDIMENTO_REPORTS.NOW.REMOVED_USER') }}
              </span>
              <span class="font-medium tabular-nums text-n-slate-12">
                {{ number(person.count) }}
              </span>
            </button>
          </li>
        </ul>
        <span
          v-if="perPersonKey && withPerson.per_person.length"
          data-test-id="atendimento-agora-per-person-key"
          class="text-xs text-n-slate-10"
        >
          {{ perPersonKey }}
        </span>
      </section>

      <!-- A lista inteira, em colunas: com 30 atendentes, um cartão da linha de
           cima esticaria a altura dos outros. -->
      <section
        data-test-id="atendimento-agora-online"
        class="flex flex-col gap-1 p-3 min-w-0 rounded-lg border border-n-weak sm:col-span-2 xl:col-span-4"
      >
        <span class="text-sm text-n-slate-11">
          {{ t('ATENDIMENTO_REPORTS.NOW.ONLINE') }}
        </span>
        <span class="flex gap-1.5 items-baseline">
          <span class="text-2xl font-medium tabular-nums text-n-slate-12">
            {{ number(online.count) }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{
              t('ATENDIMENTO_REPORTS.NOW.ONLINE_OF', {
                total: number(online.total),
              })
            }}
          </span>
        </span>
        <div class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-n-slate-11">
          <span
            v-for="status in STATUS_ORDER"
            :key="status"
            :data-test-id="`atendimento-agora-status-${status}`"
            class="flex gap-1.5 items-center"
          >
            <span class="rounded-full size-2" :class="STATUS_DOT[status]" />
            {{ statusLabel(status) }}
            <span class="font-medium tabular-nums text-n-slate-12">
              {{ number(online.by_status[status]) }}
            </span>
          </span>
        </div>
        <ul
          class="grid grid-cols-1 gap-x-4 gap-y-1 mt-2 text-sm list-none sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        >
          <li
            v-for="person in people"
            :key="person.user_id"
            class="flex gap-2 items-center min-w-0"
            :class="
              person.status === 'offline'
                ? 'text-n-slate-10'
                : 'text-n-slate-12'
            "
            :title="statusLabel(person.status)"
          >
            <span
              class="flex-shrink-0 rounded-full size-2"
              :class="STATUS_DOT[person.status]"
            />
            <span class="truncate">
              {{
                person.teams.length
                  ? t('ATENDIMENTO_REPORTS.NOW.PERSON_TEAMS', {
                      name: person.name,
                      teams: person.teams.map(teamLabel).join(', '),
                    })
                  : person.name
              }}
            </span>
          </li>
        </ul>
      </section>
    </div>

    <AgoraConversas ref="listRef" :filters="filters" />
  </AnalyticsPanel>
</template>
